<template>
  <div>
    <!-- Object: render each key as its own labelled block -->
    <template v-if="kind === 'object'">
      <div v-for="key in objectKeys" :key="key" class="field-block">
        <!-- scalar value -> text field -->
        <v-text-field v-if="valueKind(data[key]) === 'scalar'" :value="displayScalar(data[key])" :label="humanize(key)"
          readonly dense hide-details></v-text-field>

        <!-- array of objects -> table ; array of scalars -> chips ; nested object -> sub-section -->
        <div v-else>
          <div class="section-title">{{ humanize(key) }}</div>
          <div class="section-body">
            <jsonView :data="data[key]" />
          </div>
        </div>
      </div>
    </template>

    <!-- Array of complex objects: render each element stacked, so nested tables/objects
         (e.g. turnover, reviews) fall onto their own line instead of cramped table cells -->
    <template v-else-if="kind === 'objectlist'">
      <div v-for="(item, i) in data" :key="i" class="object-item">
        <jsonView :data="item" />
      </div>
    </template>

    <!-- Array of flat objects: render as a table -->
    <template v-else-if="kind === 'table'">
      <v-simple-table dense class="mb-2">
        <template v-slot:default>
          <thead>
            <tr>
              <th v-for="col in tableColumns" :key="col">{{ humanize(col) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in data" :key="i">
              <td v-for="col in tableColumns" :key="col" class="td-top">
                <span v-if="valueKind(row[col]) === 'scalar'">{{ displayScalar(row[col]) }}</span>
                <jsonView v-else :data="row[col]" />
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </template>

    <!-- Array of scalars: render as chips -->
    <template v-else-if="kind === 'list'">
      <div class="mb-2">
        <v-chip v-for="(item, i) in data" :key="i" small label class="mr-1 mb-1">{{ displayScalar(item) }}</v-chip>
        <span v-if="data.length === 0" class="empty">—</span>
      </div>
    </template>

    <!-- Fallback scalar -->
    <template v-else>
      <span>{{ displayScalar(data) }}</span>
    </template>
  </div>
</template>

<script>
export default {
  name: 'jsonView', // required for the component to recurse into itself
  props: {
    data: {
      required: true
    }
  },
  computed: {
    kind() {
      return this.valueKind(this.data);
    },
    objectKeys() {
      return this.data && typeof this.data === 'object' ? Object.keys(this.data) : [];
    },
    // union of all keys across the rows, preserving first-seen order
    tableColumns() {
      const cols = [];
      (this.data || []).forEach(row => {
        if (row && typeof row === 'object') {
          Object.keys(row).forEach(k => { if (!cols.includes(k)) cols.push(k); });
        }
      });
      return cols;
    }
  },
  methods: {
    valueKind(val) {
      if (Array.isArray(val)) {
        const objs = val.filter(v => v && typeof v === 'object' && !Array.isArray(v));
        if (objs.length === 0) return 'list'; // scalars only (or empty)
        // flat = every object element has only scalar properties -> nice table (e.g. turnover)
        const allFlat = objs.every(o => Object.keys(o).every(k => {
          const cv = o[k];
          return cv === null || typeof cv !== 'object';
        }));
        return allFlat ? 'table' : 'objectlist';
      }
      if (val !== null && typeof val === 'object') {
        return 'object';
      }
      return 'scalar';
    },
    displayScalar(val) {
      if (val === null || val === undefined || val === '') return '—';
      if (typeof val === 'boolean') return val ? 'Yes' : 'No';
      return String(val);
    },
    // turn "eignNaam" / "eign_naam" into "Eign naam"
    humanize(key) {
      return String(key)
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/^\s*./, c => c.toUpperCase())
        .trim();
    }
  }
};
</script>

<style scoped>
.field-block {
  margin-bottom: 18px;
}
.section-title {
  font-weight: 600;
  font-size: 13px;
  margin: 8px 0 12px 0;
}
.section-body {
  border-left: 2px solid #e0e0e0;
  padding-left: 12px;
  margin-top: 8px;
}
.object-item {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 16px;
}
.td-top {
  vertical-align: top;
}
.empty {
  color: #9e9e9e;
}
</style>
